/**
 * Pragmatic Intelligence Tester
 *
 * Employee testing tool with 3 tabs:
 * - Dual-Stream Tester
 * - Coach Lens Tester
 * - Accommodation Preview
 */

import { useState } from 'react'
import { motion } from 'framer-motion'
import { api } from '@personic/shared'
import type {
  DualStreamAnalysisResponse,
  CoachRenderResponse,
  AvailableAccommodationsResponse,
} from '@personic/shared'

type TesterTab = 'dual-stream' | 'coach-lens' | 'accommodations'

const testerTabs: Array<{ id: TesterTab; label: string; icon: string }> = [
  { id: 'dual-stream', label: 'Dual-Stream', icon: '🔄' },
  { id: 'coach-lens', label: 'Coach Lens', icon: '🎓' },
  { id: 'accommodations', label: 'Accommodations', icon: '🌍' },
]

export default function PragmaticTester() {
  const [activeTab, setActiveTab] = useState<TesterTab>('dual-stream')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Pragmatic Intelligence Tester</h1>
        <p className="text-gray-600 mt-1">
          Test dual-stream analysis, Coach Lens rendering, and accommodation previews
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {testerTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-copper-500 text-copper-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeTab === 'dual-stream' && <DualStreamTesterTab />}
        {activeTab === 'coach-lens' && <CoachLensTesterTab />}
        {activeTab === 'accommodations' && <AccommodationPreviewTab />}
      </motion.div>
    </div>
  )
}

// =============================================================================
// DUAL-STREAM TESTER
// =============================================================================

function DualStreamTesterTab() {
  const [sessionId, setSessionId] = useState('')
  const [invokerId, setInvokerId] = useState('')
  const [includeCrossStream, setIncludeCrossStream] = useState(true)
  const [result, setResult] = useState<DualStreamAnalysisResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showRawJson, setShowRawJson] = useState(false)

  const handleAnalyze = async () => {
    if (!sessionId || !invokerId) return
    setIsLoading(true)
    setError(null)
    try {
      const { data } = await api.post<DualStreamAnalysisResponse>(
        `/pragmatic/conversations/${sessionId}/analysis/dual-stream`,
        {
          invoker_id: invokerId,
          session_id: sessionId,
          include_cross_stream: includeCrossStream,
        }
      )
      setResult(data)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Analysis failed'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Run Dual-Stream Analysis</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Session ID</label>
            <input
              type="text"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="Enter session ID..."
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoker ID</label>
            <input
              type="text"
              value={invokerId}
              onChange={(e) => setInvokerId(e.target.value)}
              placeholder="Enter invoker (agent) ID..."
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm font-mono"
            />
          </div>
        </div>
        <div className="flex items-center gap-4 mt-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeCrossStream}
              onChange={(e) => setIncludeCrossStream(e.target.checked)}
              className="rounded border-gray-300"
            />
            Include cross-stream analysis
          </label>
          <button
            onClick={handleAnalyze}
            disabled={!sessionId || !invokerId || isLoading}
            className="px-4 py-2 bg-copper-500 text-white rounded-lg text-sm font-medium hover:bg-copper-600 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Analyzing...' : 'Run Analysis'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Analysis Results</h3>
              <button
                onClick={() => setShowRawJson(!showRawJson)}
                className="text-xs text-copper-500 hover:text-copper-600"
              >
                {showRawJson ? 'Hide' : 'Show'} Raw JSON
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Turn</p>
                <p className="text-lg font-semibold">{result.turn_number}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Dual Stream</p>
                <p className="text-lg font-semibold">
                  {result.has_dual_stream ? 'Yes' : 'No'}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Invoker / Counterparty Turns</p>
                <p className="text-lg font-semibold">
                  {result.invoker_turn_count} / {result.counterparty_turn_count}
                </p>
              </div>
            </div>

            {/* Per-stream details */}
            <div className="grid md:grid-cols-2 gap-4">
              {result.invoker && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 text-sm mb-2">Invoker Stream</h4>
                  <div className="space-y-1 text-xs text-blue-700">
                    <p>Turns: {result.invoker.turn_count}</p>
                    <p>Events: {result.invoker.events_count}</p>
                    <p>Deviations: {result.invoker.has_significant_deviations ? 'Yes' : 'No'}</p>
                    <p>Trend changes: {result.invoker.has_trend_changes ? 'Yes' : 'No'}</p>
                    {result.invoker.significant_signals.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {result.invoker.significant_signals.map((s) => (
                          <span key={s} className="px-2 py-0.5 bg-blue-100 rounded-full">{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {result.counterparty && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-medium text-amber-900 text-sm mb-2">Counterparty Stream</h4>
                  <div className="space-y-1 text-xs text-amber-700">
                    <p>Turns: {result.counterparty.turn_count}</p>
                    <p>Events: {result.counterparty.events_count}</p>
                    <p>Deviations: {result.counterparty.has_significant_deviations ? 'Yes' : 'No'}</p>
                    <p>Trend changes: {result.counterparty.has_trend_changes ? 'Yes' : 'No'}</p>
                    {result.counterparty.significant_signals.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {result.counterparty.significant_signals.map((s) => (
                          <span key={s} className="px-2 py-0.5 bg-amber-100 rounded-full">{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Cross-Stream */}
            {result.cross_stream && (
              <div className="mt-4 p-4 bg-emerald-50 rounded-lg">
                <h4 className="font-medium text-emerald-900 text-sm mb-2">Cross-Stream Analysis</h4>
                <p className="text-xs text-emerald-700 mb-2">
                  {result.cross_stream.inflections.length} inflection(s), {result.cross_stream.response_dependencies.length} dependency(ies)
                </p>
                {result.cross_stream.causal_summary && (
                  <p className="text-xs text-emerald-600 italic">{result.cross_stream.causal_summary}</p>
                )}
              </div>
            )}
          </div>

          {/* Raw JSON */}
          {showRawJson && (
            <div className="card p-4">
              <pre className="text-xs text-gray-600 overflow-auto max-h-96 font-mono">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// COACH LENS TESTER
// =============================================================================

function CoachLensTesterTab() {
  const [agentId, setAgentId] = useState('')
  const [sessionId, setSessionId] = useState('')
  const [profileId, setProfileId] = useState('')
  const [result, setResult] = useState<CoachRenderResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showRawJson, setShowRawJson] = useState(false)

  const handleRender = async () => {
    if (!agentId || !sessionId || !profileId) return
    setIsLoading(true)
    setError(null)
    try {
      const { data } = await api.post<CoachRenderResponse>(
        `/pragmatic/profile/${profileId}/render/coach`,
        {
          session_id: sessionId,
          agent_id: agentId,
          include_cross_stream: true,
        }
      )
      setResult(data)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Rendering failed'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Coach Lens Renderer</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agent ID</label>
            <input
              type="text"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              placeholder="Agent ID..."
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Session ID</label>
            <input
              type="text"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="Session ID..."
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Profile ID</label>
            <input
              type="text"
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
              placeholder="Profile ID..."
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm font-mono"
            />
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handleRender}
            disabled={!agentId || !sessionId || !profileId || isLoading}
            className="px-4 py-2 bg-copper-500 text-white rounded-lg text-sm font-medium hover:bg-copper-600 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Rendering...' : 'Render Coach Insights'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Coach Insights</h3>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-1 rounded-full ${
                  result.consent_status === 'granted'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  Consent: {result.consent_status}
                </span>
                <button
                  onClick={() => setShowRawJson(!showRawJson)}
                  className="text-xs text-copper-500"
                >
                  {showRawJson ? 'Hide' : 'Show'} JSON
                </button>
              </div>
            </div>

            {/* Summary */}
            {result.summary && (
              <div className="p-4 bg-copper-50 rounded-lg mb-4">
                <h4 className="font-medium text-copper-900 text-sm mb-1">
                  {result.summary.headline}
                </h4>
                <ul className="list-disc list-inside text-xs text-copper-700 space-y-1">
                  {result.summary.key_observations.map((obs, i) => (
                    <li key={i}>{obs}</li>
                  ))}
                </ul>
                <p className="text-xs text-copper-600 mt-2 font-medium">
                  Focus: {result.summary.recommended_focus}
                </p>
              </div>
            )}

            {/* Insights */}
            <div className="space-y-3">
              {result.insights.map((insight, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-lg border ${
                    insight.severity === 'critical'
                      ? 'bg-red-50 border-red-200'
                      : insight.severity === 'attention'
                      ? 'bg-amber-50 border-amber-200'
                      : insight.severity === 'suggestion'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-gray-500">[{insight.type}]</span>
                    <span className="text-sm font-semibold text-gray-900">{insight.title}</span>
                    {insight.actionable && (
                      <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">actionable</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-700">{insight.description}</p>
                  {insight.evidence.length > 0 && (
                    <div className="mt-1 text-xs text-gray-500 italic">
                      {insight.evidence.map((e, j) => (
                        <p key={j}>"{e}"</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {showRawJson && (
            <div className="card p-4">
              <pre className="text-xs text-gray-600 overflow-auto max-h-96 font-mono">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// ACCOMMODATION PREVIEW
// =============================================================================

function AccommodationPreviewTab() {
  const [culturalContext, setCulturalContext] = useState('')
  const [ndProfiles, setNdProfiles] = useState<string[]>([])
  const [accommodations, setAccommodations] = useState<AvailableAccommodationsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [previewResult, setPreviewResult] = useState<Record<string, unknown> | null>(null)

  const loadAccommodations = async () => {
    setIsLoading(true)
    try {
      const { data } = await api.get<AvailableAccommodationsResponse>('/pragmatic/accommodations')
      setAccommodations(data)
    } catch {
      // Handle error
    } finally {
      setIsLoading(false)
    }
  }

  const handlePreview = () => {
    // Build preview from selections
    const preview: Record<string, unknown> = {}
    if (culturalContext && accommodations) {
      const ctx = accommodations.cultural_contexts.find((c) => c.name === culturalContext)
      if (ctx) preview.cultural_adjustments = ctx.adjustments
    }
    if (ndProfiles.length > 0 && accommodations) {
      const ndAdjustments: Record<string, unknown> = {}
      ndProfiles.forEach((name) => {
        const profile = accommodations.neurodivergent_profiles.find((p) => p.name === name)
        if (profile) ndAdjustments[name] = profile.accommodations
      })
      preview.neurodivergent_adjustments = ndAdjustments
    }
    setPreviewResult(preview)
  }

  const toggleNd = (name: string) => {
    setNdProfiles((prev) =>
      prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name]
    )
  }

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Accommodation Preview</h3>
          {!accommodations && (
            <button
              onClick={loadAccommodations}
              disabled={isLoading}
              className="px-4 py-2 bg-copper-500 text-white rounded-lg text-sm font-medium hover:bg-copper-600 disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : 'Load Options'}
            </button>
          )}
        </div>

        {accommodations && (
          <div className="space-y-6">
            {/* Cultural Context */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cultural Context
              </label>
              <select
                value={culturalContext}
                onChange={(e) => setCulturalContext(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value="">None (global default)</option>
                {accommodations.cultural_contexts.map((ctx) => (
                  <option key={ctx.name} value={ctx.name}>
                    {ctx.description} ({ctx.region})
                  </option>
                ))}
              </select>
            </div>

            {/* Neurodivergent Profiles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Neurodivergent Profiles
              </label>
              <div className="flex flex-wrap gap-2">
                {accommodations.neurodivergent_profiles.map((profile) => (
                  <button
                    key={profile.name}
                    onClick={() => toggleNd(profile.name)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      ndProfiles.includes(profile.name)
                        ? 'bg-copper-50 border-copper-300 text-copper-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {profile.description}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handlePreview}
              className="px-4 py-2 bg-copper-500 text-white rounded-lg text-sm font-medium hover:bg-copper-600"
            >
              Preview Adjustments
            </button>
          </div>
        )}
      </div>

      {/* Preview Results */}
      {previewResult && (
        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Baseline Adjustment Preview</h3>
          <pre className="text-xs text-gray-600 bg-gray-50 p-4 rounded-lg overflow-auto max-h-96 font-mono">
            {JSON.stringify(previewResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}
